/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IFormValues,
  IUserSubprofileModel,
  ScreenType,
  useConfigurationSelector,
  UserStore,
  useUserSubprofilesSelector,
} from "@nf/common";
import { ROUTES } from "@nf/constants";
import { Trans, useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";

import { Form, Input } from "components";
import Button from "components/Button";
import { useParentalRatings } from "hooks";

const ActionsHandler = dynamic(() => import("components/ActionsHandler"));

const ColorPicker = dynamic(() => import("components/Subprofiles/ColorPicker"));

const Stepper = dynamic(
  () => import("components/Stepper/components/AgeStepper")
);

const RadioGroup = dynamic(() => import("components/RadioGroup"));

const Checkbox = dynamic(() => import("components/Checkbox"));

const LabelField = dynamic(() => import("components/Form/LabelField"));

const FormButton = dynamic(() => import("components/Form/FormButton"));

import Remove from "../../../resources/icons/trash.svg";

import styles from "./SubprofileForm.module.scss";

interface ISubprofileFormProps {
  editForm?: boolean;
}

export const SubprofileForm = ({ editForm }: ISubprofileFormProps) => {
  const { t, i18n } = useTranslation("translations");
  const dispatch = useDispatch();
  const router = useRouter();
  const subprofileId = router.query.id as string | number;

  const configuration = useConfigurationSelector();
  const { data: parentalRatings, isLoading: isLoadingParentalRatings } =
    useParentalRatings();
  const { Data } = useUserSubprofilesSelector();

  const selectedSubprofile = Data?.subprofiles?.find(
    (sub) => subprofileId === sub.UserId
  );
  const isOwner = selectedSubprofile?.Owner;
  const isCurrentProfile =
    Data?.currentSubprofile?.UserId === selectedSubprofile?.UserId;
  const canBeDeleted = editForm && !isOwner && !isCurrentProfile;

  const screens =
    configuration?.Screens?.[ScreenType.ApplicationMenu]?.Components?.map(
      (page) => ({
        value: page.Id,
        label: page.Title,
      })
    ) || [];

  const [form] = Form.useForm();
  const initialValues: IFormValues = {
    profileName: selectedSubprofile?.DisplayName,
    child: selectedSubprofile?.Child,
    color: selectedSubprofile?.Color,
    initialHomePage: selectedSubprofile?.InitialHomePage,
  };

  const profileDelete = () => {
    dispatch(UserStore.Actions.deleteUserSubprofile(subprofileId));
    router.back();
  };

  const onFinish = (values: IFormValues) => {
    const subprofilePayload: IUserSubprofileModel = {
      UserId: String(subprofileId),
      DisplayName: values["profileName"],
      Child: values["child"],
      ProfileType: values["child"] ? values["profileType"] : "",
      Language: i18n.language,
      Color: values["color"],
      InitialHomePage: values["initialHomePage"],
      CurrentProfile: isCurrentProfile,
    };

    if (editForm) {
      dispatch(
        UserStore.Actions.updateUserSubprofile(subprofilePayload, subprofileId)
      );
    } else {
      delete subprofilePayload.UserId;
      dispatch(UserStore.Actions.createUserSubprofiles(subprofilePayload));
    }

    router.back();
  };

  return (
    <>
      <h2 className={styles.header}>
        {editForm
          ? t("web_subprofiles_edit_profile")
          : t("web_subprofiles_add_profile")}
      </h2>

      <Form
        className={styles.form}
        initialValues={initialValues}
        form={form}
        onFinish={onFinish}
      >
        <ColorPicker initialColor={selectedSubprofile?.Color} />

        <LabelField label={t("web_subprofiles_name")} name="profileName">
          <Input placeholder={t("web_subprofiles_placeholder")} />
        </LabelField>

        {!isOwner && (
          <label className={styles.checkbox}>
            <Form.Field name="child">
              {({ value, onChange }) => (
                <Checkbox
                  checked={value}
                  onChange={(e) => {
                    onChange((e.target as HTMLInputElement).checked);
                  }}
                />
              )}
            </Form.Field>
            <span>{t("web_subprofiles_kids_profile")}</span>
          </label>
        )}

        <Form.Field shouldUpdate={(prev, next) => prev.child !== next.child}>
          {({ value }) => {
            const isChild = value.child;

            if (isChild) {
              return null;
            }

            return (
              <div className={styles.initialPageContainer}>
                <p>{t("web_profile_startpage_header")}</p>
                <span className={styles.initialPageContainer__description}>
                  {t("web_profile_startpage_text")}
                </span>

                <Form.Field name="initialHomePage">
                  <RadioGroup name="initialHomePage" options={screens} />
                </Form.Field>
              </div>
            );
          }}
        </Form.Field>

        <Form.Field shouldUpdate={(prev, next) => prev.child !== next.child}>
          {({ value }) => {
            const isChild = value.child;

            if (!isChild || isOwner) {
              return null;
            }

            return (
              <>
                <p className={styles.ageRestriction}>
                  {t("web_subprofiles_age_restriction")}
                </p>

                <ActionsHandler
                  isLoading={
                    !parentalRatings?.length && isLoadingParentalRatings
                  }
                >
                  {!!parentalRatings?.length ? (
                    <>
                      <Form.Field
                        name="profileType"
                        initialValue={
                          selectedSubprofile?.ProfileType ||
                          parentalRatings?.[0].ratings
                        }
                      >
                        <Stepper
                          steps={parentalRatings.map((rating) => ({
                            value: rating.ratings,
                            label: `${rating.label} ${t(
                              "web_subprofiles_kids_years"
                            )}`,
                          }))}
                        />
                      </Form.Field>

                      <Trans
                        i18nKey={t("web_subprofiles_kids_profile_reminder")}
                        components={{
                          p: <p className={styles.ageRestrictionText} />,
                          br: <br />,
                        }}
                      />
                    </>
                  ) : undefined}
                </ActionsHandler>
              </>
            );
          }}
        </Form.Field>

        <div className={styles.buttons}>
          {canBeDeleted ? (
            <Button
              type="button"
              size="medium"
              variant="subtle"
              icon={<Remove />}
              className={styles.button}
              onClick={profileDelete}
            >
              {t("web_subprofiles_button_delete_profile")}
            </Button>
          ) : (
            <Button
              size="medium"
              variant="subtle"
              className={styles.button}
              href={ROUTES.SETTINGS_SUBPROFILE_SCREEN}
            >
              {t("web_cancel")}
            </Button>
          )}

          <FormButton
            nonEmptyFields={{
              requiredFields: ["profileName"],
              autoCompleted: false,
            }}
            className={styles.button}
          >
            {editForm
              ? t("web_subprofiles_button_save_changes")
              : t("web_subprofiles_button_save")}
          </FormButton>
        </div>
      </Form>
    </>
  );
};

export default SubprofileForm;
